import { Language, LoaderType, StorageKey } from '@shared/enum';
import { handleError } from '@shared/functions';
import { localStorageService } from '@shared/localStorageService';
import { useAppDispatch, useAppSelector } from '@state/configureStore';
import { disableLoading, enableLoading, selectBaseInfo, selectLanguage, setBaseInfo } from '@state/pageSlice';
import { useGetUserBaseInfoQuery, UserRole } from '@state/userApi';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routes } from '../app/constant';
import { useAlert } from '../components/Alert/hooks/useAlert';

export const useUserBaseInfo = () => {
    const [skip, setSkip] = useState(true);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const language = useAppSelector(selectLanguage);
    const baseInfo = useAppSelector(selectBaseInfo);
    const navigate = useNavigate();

    const {
        data: dataUserBaseInfo,
        error: errorUserBaseInfo,
        isFetching: isLoadingUserBaseInfo,
    } = useGetUserBaseInfoQuery({ language: language }, { skip: skip });

    const { error } = useAlert();

    const execute = () => {
        if (baseInfo.fullname && baseInfo.language) {
            setSkip(true);
        } else {
            setSkip(false);
        }
    };

    useEffect(() => {
        if (errorUserBaseInfo) {
            setSkip(true);
            const data = handleError(errorUserBaseInfo);
            if (data.redirect) {
                navigate(routes.error500);
            } else if (data.msg) {
                error(data.msg);
            } else if (data.key) {
                error(t(data.key));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorUserBaseInfo, error, t]);

    useEffect(() => {
        if (isLoadingUserBaseInfo) {
            dispatch(enableLoading(LoaderType.userBase));
        } else {
            dispatch(disableLoading(LoaderType.userBase));
        }
    }, [isLoadingUserBaseInfo, dispatch]);

    useEffect(() => {
        if (dataUserBaseInfo && dataUserBaseInfo.data) {
            const langEnum =
                Language[dataUserBaseInfo.data.language.toUpperCase() as keyof typeof Language] ?? Language.EN;
            const result = {
                fullname: dataUserBaseInfo.data.full_name,
                language: langEnum,
                userRole: dataUserBaseInfo.data.role_names as UserRole,
            };

            localStorageService.setItem(StorageKey.baseInfo, JSON.stringify(result));
            dispatch(
                setBaseInfo({
                    fullname: dataUserBaseInfo.data.full_name,
                    language: langEnum,
                    userRole: dataUserBaseInfo.data.role_names as UserRole,
                }),
            );
            setSkip(true);
        }
    }, [dataUserBaseInfo, dispatch]);

    return [execute] as const;
};
